/* eslint-disable no-param-reassign, @typescript-eslint/no-unused-vars */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { GetAllUnitsByFormulaTypeAPI } from '../../../constants/ConversionCalculator';
import {
  FIELD_NOMS_DEADLINE_NOTIFICATION,
  GET_PRODUCTION_FIELDS,
  GET_SOURCE_PRODUCTION_FIELDS,
} from '../../../constants/FieldNominations';
import { get as CopGet } from '../../../services/COPApi';
import { get } from '../../../services/GenesisApi';
import { ISelectOptions } from '../../../types/Common.type';
import { getFormulaForDisplay } from '../../../utils/conversion_calculator';

interface INotifications {
  note: string;
  timer: string;
}

interface ISelectField {
  loading: boolean;
  data: ISelectOptions[];
  error: boolean;
}

export interface IFormula {
  id: number;
  name: string;
  formula: string;
}

const selectInitialData = {
  loading: false,
  data: [],
  error: false,
};

export interface IFieldNomsState {
  productionField: ISelectField;
  sourceProductionField: ISelectField;
  terminal: ISelectField;
  subTerminal: ISelectField;
  uploadToAligne: {
    loading: boolean;
    uploadStatus: 'Success' | 'Failed' | null;
    error: boolean;
  };
  formulaData: {
    loading: boolean;
    data: IFormula[];
    error: boolean;
  };
  notifications: {
    loading: boolean;
    count: number;
    data: INotifications[];
    error: boolean;
  };
}

const initialState: IFieldNomsState = {
  productionField: selectInitialData,
  sourceProductionField: selectInitialData,
  terminal: selectInitialData,
  subTerminal: selectInitialData,
  uploadToAligne: {
    loading: false,
    uploadStatus: null,
    error: false,
  },
  formulaData: {
    loading: false,
    data: [],
    error: false,
  },
  notifications: {
    loading: false,
    count: 0,
    data: [],
    error: false,
  },
};

export const getProductionFields = createAsyncThunk(
  'fieldNoms/getProductionFields',
  async () =>
    get(GET_PRODUCTION_FIELDS).then((resp) => {
      return resp.data;
    })
);

export const getSourceProductionFields = createAsyncThunk(
  'fieldNoms/getSourceProductionFields',
  async () =>
    get(GET_SOURCE_PRODUCTION_FIELDS).then((resp) => {
      return resp.data;
    })
);

export const getFormula = createAsyncThunk('fieldNoms/getFormula', async () =>
  get(GetAllUnitsByFormulaTypeAPI).then((resp) => {
    const responseData = resp.data ?? [];
    return responseData.map((item) => ({
      id: item.id,
      name: item.formulaName,
      formula: getFormulaForDisplay(item),
    }));
  })
);

export const getDeadlineNotifications = createAsyncThunk(
  'fieldNoms/getDeadlineNotifications',
  async (date: string) => {
    return CopGet(`${FIELD_NOMS_DEADLINE_NOTIFICATION}/${date}`)
      .then((resp) => {
        return resp.data ?? [];
      })
      .catch((err) => {
        throw err;
      });
  }
);

const fieldNomsSlice = createSlice({
  name: 'fieldNoms',
  initialState,
  reducers: {
    clearUploadResponse: (state) => {
      state.uploadToAligne.loading = false;
      state.uploadToAligne.uploadStatus = null;
    },
    updateProdFields: (
      state,
      action: PayloadAction<{
        prodFieldId: number;
        sourceProdFieldId: number;
        status: boolean;
      }>
    ) => {
      const { prodFieldId, sourceProdFieldId, status } = action.payload;

      const updateFieldVisibility = (
        fields: ISelectOptions[],
        fieldId: number
      ) =>
        fields.map((field) =>
          field.id === fieldId ? { ...field, isVisible: status } : field
        );

      state.productionField.data = updateFieldVisibility(
        state.productionField.data,
        prodFieldId
      );
      state.sourceProductionField.data = updateFieldVisibility(
        state.sourceProductionField.data,
        sourceProdFieldId
      );
    },
  },
  extraReducers: (builder) => {
    // productionField
    builder
      .addCase(getProductionFields.pending, (state: IFieldNomsState) => {
        state.productionField.loading = true;
        state.productionField.data = [];
        state.productionField.error = false;
      })
      .addCase(
        getProductionFields.fulfilled,
        (
          state: IFieldNomsState,
          { payload }: PayloadAction<ISelectOptions[]>
        ) => {
          state.productionField.loading = false;
          state.productionField.data = payload;
          state.productionField.error = false;
        }
      )
      .addCase(getProductionFields.rejected, (state: IFieldNomsState) => {
        state.productionField.loading = false;
        state.productionField.data = [];
        state.productionField.error = true;
      });

    // sourceProductionField
    builder
      .addCase(getSourceProductionFields.pending, (state: IFieldNomsState) => {
        state.sourceProductionField.loading = true;
        state.sourceProductionField.data = [];
        state.sourceProductionField.error = false;
      })
      .addCase(
        getSourceProductionFields.fulfilled,
        (
          state: IFieldNomsState,
          { payload }: PayloadAction<ISelectOptions[]>
        ) => {
          state.sourceProductionField.loading = false;
          state.sourceProductionField.data = payload;
          state.sourceProductionField.error = false;
        }
      )
      .addCase(getSourceProductionFields.rejected, (state: IFieldNomsState) => {
        state.sourceProductionField.loading = false;
        state.sourceProductionField.data = [];
        state.sourceProductionField.error = true;
      });

    // getFormula
    builder
      .addCase(getFormula.pending, (state: IFieldNomsState) => {
        state.formulaData.loading = true;
        state.formulaData.data = [];
        state.formulaData.error = false;
      })
      .addCase(
        getFormula.fulfilled,
        (state: IFieldNomsState, { payload }: PayloadAction<IFormula[]>) => {
          state.formulaData.loading = false;
          state.formulaData.data = payload;
          state.formulaData.error = false;
        }
      )
      .addCase(getFormula.rejected, (state: IFieldNomsState) => {
        state.formulaData.loading = false;
        state.formulaData.data = [];
        state.formulaData.error = true;
      });

    // get field noms deadline notifications
    builder
      .addCase(getDeadlineNotifications.pending, (state: IFieldNomsState) => {
        state.notifications.loading = true;
        state.notifications.data = [];
        state.notifications.count = 0;
        state.notifications.error = false;
      })
      .addCase(
        getDeadlineNotifications.fulfilled,
        (
          state: IFieldNomsState,
          { payload }: PayloadAction<INotifications[]>
        ) => {
          state.notifications.loading = false;
          state.notifications.data = payload;
          state.notifications.count = payload.length;
          state.notifications.error = false;
        }
      )
      .addCase(getDeadlineNotifications.rejected, (state: IFieldNomsState) => {
        state.notifications.loading = false;
        state.notifications.data = [];
        state.notifications.count = 0;
        state.notifications.error = true;
      });
  },
});

export const FieldNomsActions = fieldNomsSlice.actions;
export default fieldNomsSlice.reducer;
